import React from 'react';
import { useNavigate } from 'react-router-dom';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useScrabbleGameContext } from './context/ScrabbleGameContext';

function GameOverOverlay() {
  const navigate = useNavigate();
  const { runPoints, roundIndex, playAgain } = useScrabbleGameContext();

  const handleReturnToMenu = React.useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleTryAgain = React.useCallback(() => {
    playAgain();
  }, [playAgain]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 980,
        borderRadius: 3,
        border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
        backgroundColor: 'background.paper',
        padding: { xs: 1.5, md: 2.5 },
        boxShadow: (theme) =>
          `0 10px 40px ${alpha(theme.palette.error.main, 0.18)}, 0 2px 12px ${alpha(theme.palette.common.black, 0.1)}`,
        background: (theme) =>
          `linear-gradient(145deg, ${alpha(theme.palette.error.light, 0.12)} 0%, ${alpha(
            theme.palette.background.paper,
            0.97
          )} 60%)`,
      }}
    >
      <Stack spacing={2} alignItems="center">
        <Stack spacing={1} alignItems="center">
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              border: (theme) => `2px solid ${alpha(theme.palette.error.main, 0.45)}`,
              background: (theme) =>
                `radial-gradient(circle, ${alpha(theme.palette.error.light, 0.25)} 0%, ${alpha(
                  theme.palette.error.main,
                  0.08
                )} 100%)`,
              display: 'grid',
              placeItems: 'center',
              boxShadow: (theme) => `0 0 28px ${alpha(theme.palette.error.main, 0.25)}`,
            }}
          >
            <CancelRoundedIcon sx={{ fontSize: 38, color: 'error.main' }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: -0.5,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Game Over
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
          <Chip
            icon={<CancelRoundedIcon sx={{ fontSize: 16 }} />}
            label={`Round ${roundIndex}`}
            color="error"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`${Number(runPoints || 0).toLocaleString()} Run Points`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%', maxWidth: 480 }}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            startIcon={<RefreshRoundedIcon />}
            onClick={handleTryAgain}
            sx={{ fontWeight: 700 }}
          >
            Try Again
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<HomeRoundedIcon />}
            onClick={handleReturnToMenu}
            sx={{ fontWeight: 700 }}
          >
            Return to Menu
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default GameOverOverlay;
